/**
 * What the page counts down to, and how the remaining time is split up.
 *
 * Pure and DOM-free: the Pages Function in `functions/api/state.ts` imports it
 * to work out the target for a button press, so the server and every browser
 * derive the same number from the same code. That is deliberate. The
 * alternative - the browser computing its own target and POSTing it - means a
 * device with a wrong clock or an old bundle can write a nonsense target that
 * every other device then obediently displays.
 */

import { addDays, addMonths, civilOf, instantOf } from './zoned.ts'

/** How long a countdown runs. The whole premise of the site. */
export const COUNTDOWN_YEARS = 6

/**
 * Where the original countdown starts: 23 August 2026, 11:00 PM, Belgrade.
 *
 * Written as civil fields put through `instantOf` rather than as a literal
 * epoch number or an ISO string with a hard-coded `+02:00`. An ISO literal
 * would silently bake in a UTC offset that is only correct while the anchor
 * happens to fall in summer time - move the anchor to November and the wall
 * clock would read 10:00 PM with nothing to explain why.
 */
export const ORIGINAL_START = instantOf({
  year: 2026,
  month: 8,
  day: 23,
  hour: 23,
  minute: 0,
  second: 0
})

/** Where the original countdown ends: six calendar years after the anchor. */
export const ORIGINAL_TARGET = addMonths(ORIGINAL_START, COUNTDOWN_YEARS * 12)

/**
 * The four things the buttons do.
 *
 * - `original` - the countdown the site is actually about, fixed forever.
 * - `fresh`    - a new six years, measured from the moment the button was hit.
 * - `zero`     - straight to zero; the countdown is over because someone said so.
 * - `infinite` - no countdown at all; there is nothing to count down to.
 */
export type Mode = 'original' | 'fresh' | 'zero' | 'infinite'

export const MODES: readonly Mode[] = ['original', 'fresh', 'zero', 'infinite']

export function isMode(value: unknown): value is Mode {
  return typeof value === 'string' && (MODES as readonly string[]).includes(value)
}

/**
 * The entire shared state of the site. Small on purpose: it is fetched every
 * few seconds by every open tab, and there is nothing here that a client needs
 * to be told twice.
 */
export interface CountdownState {
  mode: Mode
  /** The instant being counted down to, as epoch ms. Ignored in infinite mode. */
  target: number
  /** When this state was last set, as epoch ms. Drives the "changed" notice. */
  changedAt: number
}

/** The state a button press produces. The server calls this; clients predict with it. */
export function stateForMode(mode: Mode, at: number): CountdownState {
  if (mode === 'original') return { mode, target: ORIGINAL_TARGET, changedAt: at }
  if (mode === 'fresh') return { mode, target: addMonths(at, COUNTDOWN_YEARS * 12), changedAt: at }
  // JSON turns Infinity into null, which the client rejects and the existing
  // D1 schema cannot store. Keep a finite, unused target instead: the mode,
  // never the date, determines whether there is a countdown to render.
  if (mode === 'infinite') return { mode, target: ORIGINAL_TARGET, changedAt: at }
  // `zero` is a target in the (immediate) past rather than a paused flag. One
  // less piece of state to keep consistent, and everything downstream already
  // has to handle an elapsed countdown anyway - it is what happens naturally in
  // 2032.
  return { mode, target: at, changedAt: at }
}

/** Ask the mode, not the inert target, whether there is anything to count down. */
export function isEndless(state: Pick<CountdownState, 'mode'>): boolean {
  return state.mode === 'infinite'
}

/**
 * Whether two states put the same thing on screen.
 *
 * Used to decide whether a press is worth telling anybody about
 * (`src/server/notify.ts`). `changedAt` is deliberately not compared: every
 * press writes a new one, so comparing it would call every press a change and
 * the question would not be worth asking.
 *
 * The `zero` special case is the one that is easy to get wrong. Its target is
 * "the moment the button was pressed", so two presses of *Skip to zero* store
 * two different numbers - while both render as six 00s. Comparing targets there
 * reports a change that nobody watching the page could possibly see. `infinite`
 * has the same shape for the opposite reason: its target is a placeholder that
 * is never read, so it is already equal and the branch is here to say so out
 * loud rather than by accident.
 */
export function sameDisplay(a: CountdownState, b: CountdownState): boolean {
  if (a.mode !== b.mode) return false
  if (a.mode === 'zero' || a.mode === 'infinite') return true
  return a.target === b.target
}

/** The state to show when the server has never been asked, or cannot be reached. */
export function defaultState(): CountdownState {
  return { mode: 'original', target: ORIGINAL_TARGET, changedAt: ORIGINAL_START }
}

export interface Remaining {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
  seconds: number
  /** Raw milliseconds left, before any of the splitting. Never negative. */
  totalMs: number
  /** True once the target is in the past. */
  elapsed: boolean
}

const ZERO: Remaining = {
  years: 0,
  months: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  totalMs: 0,
  elapsed: true
}

/** Average length of a Gregorian month. Only ever used as a search seed. */
const MEAN_MONTH_MS = 30.436875 * 86_400_000

/**
 * Split the time between two instants into calendar years, months and days
 * plus a clock remainder.
 *
 * The shape of this is "how many whole months fit, then how many whole days fit
 * in what is left" rather than any kind of division, because months and days
 * are not fixed lengths (see `zoned.ts`). The mean-month figure seeds the search
 * so the loops below run one or two iterations rather than seventy-two.
 *
 * Counting is anchored on `now` and moves forward, not anchored on `target` and
 * moving back. The two disagree - February to 31 March is "1 month 3 days"
 * forward and "1 month 0 days" back - and forward is the one that matches how a
 * countdown is read aloud: "a month and three days from now".
 *
 * ---------------------------------------------------------------------------
 * WHY `now` IS ROUNDED UP FIRST
 * ---------------------------------------------------------------------------
 * Truncating instead is the obvious version and it produces an embarrassing
 * result: press "start a new six years" and the display reads
 * 05:11:30:23:59:59, because by the time the digits are painted a few hundred
 * milliseconds of those six years have already gone. Technically true, useless
 * as a countdown - the button says six years and the screen says it is already
 * too late.
 *
 * Rounding the remaining time UP to the next whole second fixes both ends at
 * once. A fresh six years reads 06:00:00:00:00:00 for its first second, and the
 * final second before zero reads "1" rather than "0" - which is how every
 * countdown that has ever been read out loud works. Zero appears when the time
 * is actually up, not a second early.
 *
 * The alignment also makes the split exact: `now` is moved onto the same
 * whole-second grid as `target`, so the pieces reassemble into `target` with no
 * remainder at all. `test/countdown.test.mjs` asserts that.
 */
export function remaining(now: number, target: number): Remaining {
  if (!Number.isFinite(now) || !Number.isFinite(target) || target <= now) return ZERO

  const totalMs = target - now
  const from = target - Math.ceil(totalMs / 1_000) * 1_000

  let months = Math.max(0, Math.floor((target - from) / MEAN_MONTH_MS))
  while (months > 0 && addMonths(from, months) > target) months -= 1
  while (addMonths(from, months + 1) <= target) months += 1

  const afterMonths = addMonths(from, months)

  let days = Math.max(0, Math.floor((target - afterMonths) / 86_400_000))
  while (days > 0 && addDays(afterMonths, days) > target) days -= 1
  while (addDays(afterMonths, days + 1) <= target) days += 1

  let rest = target - addDays(afterMonths, days)
  const hours = Math.floor(rest / 3_600_000)
  rest -= hours * 3_600_000
  const minutes = Math.floor(rest / 60_000)
  rest -= minutes * 60_000
  const seconds = Math.floor(rest / 1_000)

  return {
    years: Math.floor(months / 12),
    months: months % 12,
    days,
    hours,
    minutes,
    seconds,
    totalMs,
    elapsed: false
  }
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

/**
 * "23 August 2032, 11:00 PM" - the target written out, always in `ZONE`.
 *
 * Hand-assembled rather than handed to `Intl.DateTimeFormat` with a locale,
 * because the result sits inside an English sentence. A visitor with a German
 * browser would otherwise get "23. August 2032 um 23:00" spliced into the
 * middle of "Six years, ending ...", which is worse than being consistently in
 * one language.
 */
export function describeInstant(ms: number): string {
  const c = civilOf(ms)
  const hour12 = c.hour % 12 === 0 ? 12 : c.hour % 12
  const suffix = c.hour < 12 ? 'AM' : 'PM'
  const minutes = String(c.minute).padStart(2, '0')
  return `${c.day} ${MONTH_NAMES[c.month - 1]} ${c.year}, ${hour12}:${minutes} ${suffix}`
}
